import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TemplateVariable,
  getTemplateVariables,
  createTemplateVariable,
  updateTemplateVariable,
  deprecateTemplateVariable
} from '../../lib/variables/service';
import { migrateVariableGroupsToFirestore } from '../../lib/variables/migration';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import {
  Search, Plus, Edit2, AlertCircle, Ban, CheckCircle,
  Hash, Filter, RefreshCw, X, Loader
} from 'lucide-react';
import Modal from '../shared/Modal';

const DEFAULT_GROUPS = [
  'Applicant',
  'Address',
  'Passport & Visa',
  'Program & Placement',
  'Education',
  'Employment',
  'Emergency Contacts',
  'Photo',
  'System & Dates',
  'Custom'
];

export default function VariableManagement() {
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [showDeprecated, setShowDeprecated] = useState(false);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingVar, setEditingVar] = useState<TemplateVariable | null>(null);
  const [saving, setSaving] = useState(false);

  // Form Fields
  const [formName, setFormName] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formGroup, setFormGroup] = useState('Applicant');
  const [customGroup, setCustomGroup] = useState('');
  const [formDataPath, setFormDataPath] = useState('');
  const [formSampleValue, setFormSampleValue] = useState('');
  const [formError, setFormError] = useState('');

  const fetchVars = useCallback(async () => {
    setLoading(true);
    try {
      await migrateVariableGroupsToFirestore();
      const list = await getTemplateVariables(true);
      setVariables(list);
    } catch (err) {
      console.error('Failed to load template variables:', err);
      notifyToast('error', 'Failed to load template variables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVars();
  }, [fetchVars]);

  const openCreateModal = () => {
    setEditingVar(null);
    setFormName('');
    setFormLabel('');
    setFormGroup('Applicant');
    setCustomGroup('');
    setFormDataPath('');
    setFormSampleValue('');
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (v: TemplateVariable) => {
    setEditingVar(v);
    setFormName(v.name);
    setFormLabel(v.label);
    if (DEFAULT_GROUPS.includes(v.group)) {
      setFormGroup(v.group);
      setCustomGroup('');
    } else {
      setFormGroup('Custom');
      setCustomGroup(v.group);
    }
    setFormDataPath(v.dataPath || '');
    setFormSampleValue(v.sampleValue || '');
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const cleanName = formName.trim();
    const cleanLabel = formLabel.trim();
    const finalGroup = formGroup === 'Custom' ? customGroup.trim() : formGroup;

    if (!cleanName) {
      setFormError('Variable name is required');
      return;
    }

    if (!/^[a-zA-Z0-9._-]+$/.test(cleanName)) {
      setFormError('Variable name can only contain letters, numbers, dots, hyphens, and underscores');
      return;
    }

    if (!cleanLabel) {
      setFormError('Label is required');
      return;
    }

    if (!finalGroup) {
      setFormError('Group is required');
      return;
    }

    // Check duplicate name
    const existing = variables.find(
      (v) => v.name.toLowerCase() === cleanName.toLowerCase() && v.id !== editingVar?.id
    );
    if (existing) {
      setFormError(`A variable with name "${cleanName}" already exists`);
      return;
    }

    setSaving(true);
    try {
      if (editingVar) {
        await updateTemplateVariable(editingVar.id, {
          name: cleanName,
          label: cleanLabel,
          group: finalGroup,
          dataPath: formDataPath.trim() || null,
          sampleValue: formSampleValue.trim(),
        });
        notifyToast('success', `Variable "${cleanName}" updated`);
      } else {
        await createTemplateVariable({
          name: cleanName,
          label: cleanLabel,
          group: finalGroup,
          dataPath: formDataPath.trim() || null,
          sampleValue: formSampleValue.trim(),
          deprecated: false,
        });
        notifyToast('success', `Variable "${cleanName}" created`);
      }
      setShowModal(false);
      fetchVars();
    } catch (err: any) {
      console.error('Save variable error:', err);
      setFormError(err.message || 'Failed to save variable');
    } finally {
      setSaving(false);
    }
  };

  const handleDeprecate = async (v: TemplateVariable) => {
    if (!(await confirmDialog(`Are you sure you want to deprecate {{${v.name}}}? Existing templates using it will display a warning.`))) {
      return;
    }
    try {
      await deprecateTemplateVariable(v.id);
      notifyToast('success', `Variable {{${v.name}}} deprecated`);
      fetchVars();
    } catch (err) {
      console.error('Deprecate error:', err);
      notifyToast('error', 'Failed to deprecate variable');
    }
  };

  const filtered = useMemo(() => {
    return variables.filter((v) => {
      if (!showDeprecated && v.deprecated) return false;
      if (groupFilter !== 'all' && v.group !== groupFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          v.name.toLowerCase().includes(q) ||
          v.label.toLowerCase().includes(q) ||
          (v.dataPath && v.dataPath.toLowerCase().includes(q)) ||
          v.group.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [variables, search, groupFilter, showDeprecated]);

  const groupsList = useMemo(() => {
    const set = new Set<string>();
    variables.forEach((v) => set.add(v.group));
    return Array.from(set).sort();
  }, [variables]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">Variable Registry</h2>
          <p className="text-caption text-slate-500 mt-0.5">
            Admin-managed fields available in document templates and autocomplete
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2.5 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Add Variable
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex flex-1 items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search variables by name, label, or data path..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              <option value="all">All Groups</option>
              {groupsList.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={showDeprecated}
              onChange={(e) => setShowDeprecated(e.target.checked)}
              className="rounded text-brand focus:ring-brand-primary/30 h-4 w-4"
            />
            Show Deprecated
          </label>
          <button
            onClick={fetchVars}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
            title="Refresh Registry"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Loader className="h-6 w-6 text-brand animate-spin" />
            <p className="text-xs font-medium text-slate-400">Loading variable registry...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Hash className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-700">No variables found</p>
            <p className="text-xs text-slate-500 mt-1">Try matching search terms or add a new variable.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Token Name</th>
                  <th className="px-4 py-3">Label</th>
                  <th className="px-4 py-3">Group</th>
                  <th className="px-4 py-3">Data Path</th>
                  <th className="px-4 py-3">Sample Value</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filtered.map((v) => (
                  <tr
                    key={v.id}
                    className={`hover:bg-slate-50/80 transition-colors ${
                      v.deprecated ? 'bg-amber-50/30 opacity-75' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono font-bold text-purple-700">
                      <div className="flex items-center gap-1.5">
                        <span>{`{{${v.name}}}`}</span>
                        {v.deprecated && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[11px] font-sans font-bold rounded">
                            Deprecated
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">{v.label}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-bold rounded-lg">
                        {v.group}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-500">
                      {v.dataPath ? v.dataPath : <span className="text-slate-300 italic">None (manual/computed)</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{v.sampleValue || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(v)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
                          title="Edit variable"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        {!v.deprecated && (
                          <button
                            onClick={() => handleDeprecate(v)}
                            className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                            title="Deprecate variable"
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
              <h3 className="text-sm font-extrabold text-slate-900">
                {editingVar ? `Edit Variable: {{${editingVar.name}}}` : 'Create New Variable'}
              </h3>
              <button aria-label="Close" title="Close" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {formError && (
                <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-2 text-rose-600 text-xs font-medium">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Variable Token Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. hostSupervisorName"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                />
                <p className="text-[11px] text-slate-500 mt-1">This forms the Handlebars tag e.g. {'{{hostSupervisorName}}'}</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Human-Readable Label <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Host Supervisor Name"
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Group <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formGroup}
                    onChange={(e) => setFormGroup(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  >
                    {DEFAULT_GROUPS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                {formGroup === 'Custom' && (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Custom Group Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Sponsor Details"
                      value={customGroup}
                      onChange={(e) => setCustomGroup(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Firestore Application Data Path <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. personalDetails.firstName or hostCompany.supervisor"
                  value={formDataPath}
                  onChange={(e) => setFormDataPath(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                />
                <p className="text-[11px] text-slate-500 mt-1">Leave empty for computed or manual fields.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Sample Value for Previews
                </label>
                <input
                  type="text"
                  placeholder="e.g. Jane Smith"
                  value={formSampleValue}
                  onChange={(e) => setFormSampleValue(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                />
              </div>

              <div className="flex gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm"
                >
                  {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : null}
                  {editingVar ? 'Save Changes' : 'Create Variable'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
